import { css } from '@emotion/react';
import { Link } from '@mui/material';
import { styled } from '@mui/system';
import { useAtomValue } from 'jotai';
import { noop } from 'lodash-es';
import { startTransition, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { PermissionGatedWrapper } from '../../../components/Fallback/PermissionGatedWrapper';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { FormFactoryNoLayout } from '../../../components/Form/FormFactory';
import { useFundIdField } from '../../../data-fields/CommonFields';
import { createFundDataModel } from '../../../data-models/fund.data-model';
import {
  createMetricsFundDataModel,
  IMetricFundDataModel,
  IMetricsDataModel,
} from '../../../data-models/metrics.data-model';
import { CompanyFundMetricsDataModel } from '../../../schemas/CompanyFundMetrics.schema';
import { fundsAtom } from '../../../services/state/AppConfigStateJ';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { companyMetricsAtomJ } from '../../../services/state/CompanyMetricsStateJ';
import { cardStyles } from '../../../theme/component-styles';
import { FDMap } from '../../../util/data-structure/FDMap';
import { useShowSummaryV2 } from './useShowSummaryV2';
import { createForm, IFormField } from '../../../view-models/form.view-model';
import { selectedMetricsFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { DummyCompanyId } from '../../AdminPanel/DataDisplay/DummyData';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { useUpdateInvestmentData } from '../../CompanyProfiles/hooks/useUpdateInvestmentData';
import { UnstyledFieldset } from '../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import {
  CompanyDetailsPanelBreakpoint,
  FieldsPerPage,
  IProfilePanelProps,
  usePanelPagination,
} from './CompanyDetailsPanel';
import { SecondaryForbiddenMessage } from './constants/forbidden-messages.constants';
import { PaneContents, PanelContainer, PanelPagination, PanelTitle } from './Panel';
import { SummarySection } from './SummarySection';
import { useCompanyEditPerms } from './useEditPerms';
import { withDashPlaceholder } from './withDashPlaceholder';
import {
  AllFundsId,
  fundTypeDataToFundData,
  getFundTypeOptions,
  useInvestmentMetricsFields,
} from './useInvestmentMetricsFields';

// V1's Investment Metrics header: a 3-column grid so the fund picker sits between the title and the
// paginator. V2 puts those in SubSection's slots instead.
const InvestmentMetricsPanelHeader = styled('div')`
  align-items: center;
  column-gap: 1rem;
  display: grid;
  grid-template-areas: 'title fund-selector spacer pages';
  grid-template-columns: max-content 1fr max-content;
  padding: 0 0.75rem;
`;

const InvestmentMetricsFieldsWrapper = styled('div')`
  display: grid;
  gap: 0 2rem;
  grid-template-columns: 1fr 1fr;
  align-content: start;
  min-height: 275px;
  padding: 0 0.75rem;
`;

/**
 * The `showSummaryV2` Investment Metrics grid: four columns of fields, matching Company Details'
 * V2 grid so the two cards' columns line up down the page. The V1 wrapper stays as it is — it is
 * two columns with a 275px floor, which is what put a short card in a tall box.
 *
 * No `min-height`: the design's content is two rows tall and the card is content-height, so a floor
 * would only leave dead space (and would outlive a collapse). No horizontal padding either: the
 * card's own `summaryCardCss` already supplies the design's 12px.
 */
const investmentMetricsFieldsV2Css = css`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: minmax(4rem, auto);
  gap: 0 2rem;
  align-content: start;
  @container fd-panel (width < 780px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container fd-panel (width < ${CompanyDetailsPanelBreakpoint}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/** The fund picker's width in the V2 header, per design. */
const fundSelectCss = css`
  width: 180px;
`;

export function InvestmentMetricsPanel({
  companyId,
  fieldsToShow,
  defaultPage,
  editable = true,
}: IProfilePanelProps<CompanyFundMetricsDataModel>) {
  const metricsResponse = useAtomValue(companyMetricsAtomJ({ companyId }));

  const investmentsByFund = useMemo(
    () => getInvestmentsByFund(metricsResponse?.metrics ?? [], metricsResponse?.metrics.at(0)),
    [metricsResponse?.metrics]
  );

  const hasInvestmentsData = investmentsByFund.size > 0;

  return hasInvestmentsData ? (
    <InvestmentMetricsPanelInner
      investmentsByFund={investmentsByFund}
      fieldsToShow={fieldsToShow}
      defaultPage={defaultPage}
      editable={editable}
    />
  ) : (
    <SummarySection title={'Investment Metrics'}>
      <NoDataMessage2 />
    </SummarySection>
  );
}

export function getInvestmentsByFund(
  data: IMetricsDataModel[],
  companyMetrics: IMetricsDataModel | undefined
) {
  const investmentsByFund = new Map<number, IMetricFundDataModel>();
  data
    .at(0)
    ?.fundData.forEach((metrics) =>
      investmentsByFund.set(metrics.fundId, { ...metrics, customData: metrics.customData ?? {} })
    );

  const familyRows = data.at(0)?.familyData ?? [];
  familyRows.forEach((metrics) =>
    investmentsByFund.set(metrics.fundId, { ...metrics, customData: metrics.customData ?? {} })
  );

  if (investmentsByFund.size > 1) {
    investmentsByFund.set(
      AllFundsId,
      createMetricsFundDataModel({
        ...(companyMetrics ?? { companyId: DummyCompanyId }),
        fundId: AllFundsId,
        id: AllFundsId,
      })
    );

    // Fund families replace the fund-type "(Combined)" rollups once the company has any.
    if (familyRows.length === 0 && companyMetrics?.fundTypeData?.length) {
      companyMetrics.fundTypeData.forEach((fundTypeData) => {
        const data = fundTypeDataToFundData(fundTypeData, companyMetrics.companyId);
        if (data) {
          investmentsByFund.set(data.id, data);
        }
      });
    }
  }
  return investmentsByFund;
}

interface IInvestmentMetricsPanelInnerProps extends Omit<
  IProfilePanelProps<CompanyFundMetricsDataModel>,
  'companyId'
> {
  investmentsByFund: Map<number, IMetricFundDataModel>;
}
function InvestmentMetricsPanelInner({
  investmentsByFund,
  fieldsToShow,
  defaultPage,
  editable = true,
}: IInvestmentMetricsPanelInnerProps) {
  const showSummaryV2 = useShowSummaryV2();
  const fundsWithAllFunds = useGetSelectableFunds(investmentsByFund);
  const [selectedFund, setSelectedFund] = useState(() => fundsWithAllFunds[0].id);
  const selectedFundData = investmentsByFund.get(selectedFund);
  const setEditableFields = useCompanyEditPerms(selectedFundData?.companyId ?? -1);
  const selectedFields = useAtomValue(selectedMetricsFieldsOrgState);
  const invFields = useInvestmentMetricsFields(fieldsToShow ?? selectedFields, selectedFund);
  const fields = setEditableFields(invFields);

  const fundListFormItem = useFundIdField({
    key: 'Select fund',
    label: '',
    disabled: !editable,
    selectableFunds: fundsWithAllFunds,
    disableClearable: true,
  });

  const { paginationConfig, visibleFields } = usePanelPagination({
    fields,
    initialPage: defaultPage,
  });
  // V1 paginates. V2 shows one page plus a "Show All (N)" link that reveals the rest in place, as
  // Company Details does — the design hides the paginator on these cards.
  const [showAll, setShowAll] = useState(false);
  const isTruncated = fields.length > FieldsPerPage;
  // A shadow family row not yet synced to a fundCompany has no id, so there is nothing to PUT to.
  const fieldsEditable = editable && selectedFundData?.id != null;

  const renderFields = (pageFields: IFormField<unknown>[]) =>
    selectedFundData ? (
      <UnstyledFieldset disabled={!fieldsEditable}>
        <InvestmentMetricsPage
          key={selectedFundData.fundId}
          fields={pageFields}
          selectedFundData={selectedFundData}
        />
      </UnstyledFieldset>
    ) : null;

  const fundFormField = useMemo(() => {
    return {
      name: 'selectedFund',
      onBlur: noop,
      onChange: (id: number) => {
        startTransition(() => {
          setSelectedFund(id);
        });
      },
      ref: noop,
      value: selectedFund,
    };
  }, [selectedFund, setSelectedFund]);

  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewMetrics]}
      fallbackMessage={SecondaryForbiddenMessage}
    >
      {showSummaryV2 ? (
        <SummarySection
          title={'Investment Metrics'}
          // `actions`, not `actionComponent`: the design puts the picker flush against the header's
          // right edge, which is the right-aligned slot. 180px is the design's width.
          actions={
            <div css={fundSelectCss}>
              <FieldFactory formField={fundListFormItem} formProps={fundFormField} />
            </div>
          }
        >
          {/* `-` for empty metrics, so a blank reads as "no data" rather than as empty space —
              matching Company Details and the Digest. V1 keeps the blanks it has on `main`. */}
          {renderFields(withDashPlaceholder(showAll ? fields : visibleFields))}
          {isTruncated && (
            <Link
              color={'secondary'}
              component='button'
              underline='hover'
              onClick={() => setShowAll((prev) => !prev)}
              sx={{ justifySelf: 'start', marginTop: '0.5rem' }}
            >
              {showAll ? 'Show Less' : `Show All (${fields.length})`}
            </Link>
          )}
        </SummarySection>
      ) : (
        // V1's hand-built header: `Panel` has no slot for the fund picker, so it composes the
        // primitives directly. Kept verbatim so the flag-off card is the one on `main`.
        <PanelContainer style={{ ...cardStyles, padding: 0 }}>
          <InvestmentMetricsPanelHeader>
            <PanelTitle key={'title'} title={'Investment Metrics'} />
            <FieldFactory formField={fundListFormItem} formProps={fundFormField} />
            <div style={{ gridColumn: '3 / -1' }}>
              <PanelPagination
                pageCount={paginationConfig.pageCount}
                onChange={paginationConfig.onChange}
                defaultPage={defaultPage}
              />
            </div>
          </InvestmentMetricsPanelHeader>
          <PaneContents>{renderFields(visibleFields)}</PaneContents>
        </PanelContainer>
      )}
    </PermissionGatedWrapper>
  );
}

export interface IInvestmentMetricsPanelContentsProps {
  fields: IFormField<unknown>[];
  selectedFundData: IMetricFundDataModel;
}

function InvestmentMetricsPage(props: IInvestmentMetricsPanelContentsProps) {
  const { fields, selectedFundData } = props;
  const showSummaryV2 = useShowSummaryV2();
  const updateFundCompany = useUpdateInvestmentData();
  const methods = useForm({
    values: selectedFundData,
  });

  const onChange = useCallback(
    async (data: Partial<IMetricFundDataModel>) => {
      await updateFundCompany(selectedFundData.id, data);
    },
    [selectedFundData.id, updateFundCompany]
  );

  const form = createForm<IMetricFundDataModel>({
    fields: FDMap.fromArray(fields, 'key'),
    variant: 'form-inline',
  });

  return (
    <FormProvider {...methods}>
      {showSummaryV2 ? (
        <div css={investmentMetricsFieldsV2Css}>
          <FormFactoryNoLayout form={form} onChange={onChange} />
        </div>
      ) : (
        <InvestmentMetricsFieldsWrapper>
          <FormFactoryNoLayout form={form} onChange={onChange} />
        </InvestmentMetricsFieldsWrapper>
      )}
    </FormProvider>
  );
}

function useGetSelectableFunds(investmentsByFund: Map<number, IMetricFundDataModel>) {
  const funds = useAtomValue(fundsAtom);
  const fundsWithData = funds.filter((fund) => investmentsByFund.has(fund.id));

  if (fundsWithData.length === 0) {
    return [createFundDataModel({ id: AllFundsId, name: 'All Funds' })];
  }

  if (fundsWithData.length === 1) {
    return fundsWithData;
  }

  const families = fundsWithData.filter((fund) => fund.fundKind === 'family');
  const members = fundsWithData.filter((fund) => fund.fundKind !== 'family');
  return [
    createFundDataModel({ id: AllFundsId, name: 'All Funds' }),
    ...families,
    ...getFundTypeOptions(investmentsByFund),
    ...members,
  ];
}
